// ============================================
// pages/supplier/SupplierDashboard.tsx
// Gunny supplier view: rows scoped by supplier_id. Supplier posts their
// invoice and claim block (bags, rate); gross/CGST/SGST/net are computed
// server-side.
// ============================================
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { AlertCircle, CheckCircle, Pencil, Eye, Upload, FileText, ExternalLink, Download, Truck, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../api/apiClient';
import { reportsAPI, gunnyUtilizationAPI, gunnyPosAPI, seasonsAPI, GunnyProformaRow } from '../../api/services';
import { GunnyStepTracker } from '../../components/gunny/GunnyStepTracker';
import { GunnyRowDetail } from '../../components/gunny/GunnyRowDetail';
import { bagTypeLabel } from '../../components/gunny/bagTypes';

interface SupplierPoItem {
  id: number;
  pacs_id: number | null;
  pacs?: { id: number; name: string; type?: string };
  bag_type: string;
  specification: string | null;
  quantity: number;
  rate: number;
}

interface SupplierPo {
  id: number;
  po_number: string;
  po_date: string;
  delivery_date: string | null;
  delivery_location: string | null;
  total_bags: number;
  total_amount: number;
  status: string;
  invoice_file_name: string | null;
  invoice_uploaded_at: string | null;
  items?: SupplierPoItem[];
}

const ACCEPTED_INVOICE_TYPES = '.pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg';
const INVOICE_MAX_BYTES = 10 * 1024 * 1024;

interface EditState {
  id: number;
  seasonId: number;
  bags_supplied: number;
  supplier_invoice_no: string;
  supplier_invoice_date: string;
  eway_bill_no: string;
  bill_submission_date: string;
  bill_raised_bags: number;
  claimed_rate_per_bag_incl_gst: number;
}

export const SupplierDashboard: React.FC = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState<GunnyProformaRow[]>([]);
  const [seasonId, setSeasonId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [detail, setDetail] = useState<GunnyProformaRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [pos, setPos] = useState<SupplierPo[]>([]);
  const [fulfillmentMap, setFulfillmentMap] = useState<Map<number, any>>(new Map());
  const [poDeliveriesMap, setPoDeliveriesMap] = useState<Map<number, any[]>>(new Map());
  const [posLoading, setPosLoading] = useState(true);
  const [posErr, setPosErr] = useState<string | null>(null);
  const [uploadingPoId, setUploadingPoId] = useState<number | null>(null);
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const [dispatchPo, setDispatchPo] = useState<SupplierPo | null>(null);
  const [dispatchData, setDispatchData] = useState<any>(null); // fulfillment payload
  type StagedDispatch = {
    po_item_id: number; pacs_id: number; pacs_name: string;
    bag_type?: string; specification?: string | null;
    bags: number;
    vehicle_number: string; dispatch_date: string;
    supplier_invoice_no: string; supplier_invoice_date: string;
    eway_bill_no: string; condition_at_dispatch: string; remarks: string;
    invoice_files: File[];
  };
  const [staged, setStaged] = useState<StagedDispatch[]>([]);
  // Single-invoice mode: one consolidated invoice covers every PACS in the trip
  const [singleInvoice, setSingleInvoice] = useState<{
    active: boolean;
    invoice_files: File[];
  }>({ active: false, invoice_files: [] });
  const sharedFileInputRef = useRef<HTMLInputElement | null>(null);
  const [dispatchForm, setDispatchForm] = useState<{
    pacs_id: number; bags: number;
    vehicle_number: string; dispatch_date: string;
    supplier_invoice_no: string; supplier_invoice_date: string;
    eway_bill_no: string; condition_at_dispatch: string; remarks: string;
    invoice_files: File[];
  }>({
    pacs_id: 0, bags: 0,
    vehicle_number: '', dispatch_date: new Date().toISOString().slice(0, 10),
    supplier_invoice_no: '', supplier_invoice_date: new Date().toISOString().slice(0, 10),
    eway_bill_no: '', condition_at_dispatch: '', remarks: '', invoice_files: [],
  });
  const [rowInputs, setRowInputs] = useState<Record<number, { bags: number; invoice_files: File[] }>>({});
  const [savingDispatch, setSavingDispatch] = useState(false);

  const load = useCallback(async (sid: number) => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await reportsAPI.gunniesProforma({ season_id: sid });
      setRows(data.rows);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Failed to load your claims');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPos = useCallback(async (sid: number) => {
    setPosLoading(true);
    setPosErr(null);
    try {
      const { data } = await gunnyPosAPI.list(sid);
      const list = data as SupplierPo[];
      setPos(list);
      // Fetch per-PACS fulfillment for each PO so we can show dispatched/remaining per row.
      const fulMap = new Map<number, any>();
      const delMap = new Map<number, any[]>();
      const eligible = (p: SupplierPo) => ['manager_approved', 'fulfilled', 'closed'].includes(p.status);
      await Promise.all(list.map(async (p) => {
        try {
          const { data: f } = await gunnyPosAPI.fulfillment(sid, p.id);
          fulMap.set(p.id, f);
        } catch { /* skip */ }
        if (eligible(p)) {
          try {
            const { data: d } = await gunnyPosAPI.listDeliveries(sid, p.id);
            delMap.set(p.id, d as any[]);
          } catch { /* skip */ }
        }
      }));
      setFulfillmentMap(fulMap);
      setPoDeliveriesMap(delMap);
    } catch (e: any) {
      setPosErr(e?.response?.data?.message || 'Failed to load your purchase orders');
    } finally {
      setPosLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await seasonsAPI.active();
        const sid = data?.id;
        if (!sid) {
          setErr('No active season configured');
          setLoading(false);
          setPosLoading(false);
          return;
        }
        setSeasonId(sid);
        load(sid);
        loadPos(sid);
      } catch (e: any) {
        setErr(e?.response?.data?.message || 'Failed to load the active season');
        setLoading(false);
        setPosLoading(false);
      }
    })();
  }, [load, loadPos]);

  const onPickFile = (poId: number) => {
    fileInputRefs.current[poId]?.click();
  };

  const onFileSelected = async (po: SupplierPo, file: File | null) => {
    if (!file || !seasonId) return;
    if (file.size > INVOICE_MAX_BYTES) {
      setPosErr('Invoice file must be 10 MB or smaller');
      return;
    }
    setUploadingPoId(po.id);
    setPosErr(null);
    try {
      await gunnyPosAPI.uploadInvoice(seasonId, po.id, file);
      setFlash(`Invoice uploaded for PO ${po.po_number}`);
      await loadPos(seasonId);
      setTimeout(() => setFlash(null), 2500);
    } catch (e: any) {
      setPosErr(e?.response?.data?.message || 'Invoice upload failed');
    } finally {
      setUploadingPoId(null);
      // Reset the input so re-selecting the same file fires onChange again
      if (fileInputRefs.current[po.id]) fileInputRefs.current[po.id]!.value = '';
    }
  };

  const viewInvoice = async (po: SupplierPo) => {
    if (!seasonId) return;
    try {
      const res = await apiClient.get(`/gunny-pos/${seasonId}/${po.id}/invoice`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data as Blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (e: any) {
      setPosErr(e?.response?.data?.message || 'Could not open invoice');
    }
  };

  const openDispatch = async (po: SupplierPo) => {
    if (!seasonId) return;
    setPosErr(null);
    try {
      const { data } = await gunnyPosAPI.fulfillment(seasonId, po.id);
      setDispatchPo(po);
      setDispatchData(data);
      setStaged([]);
      setSingleInvoice({ active: false, invoice_files: [] });
      setDispatchForm({
        pacs_id: data.per_pacs?.[0]?.pacs_id || 0, bags: 0,
        vehicle_number: '', dispatch_date: new Date().toISOString().slice(0, 10),
        supplier_invoice_no: '', supplier_invoice_date: new Date().toISOString().slice(0, 10),
        eway_bill_no: '', condition_at_dispatch: '', remarks: '', invoice_files: [],
      });
    } catch (e: any) {
      setPosErr(e?.response?.data?.message || 'Could not load PO allocations');
    }
  };

  // Effective remaining for a line item = server.remaining − sum already staged for that line item
  const remainingFor = (poItemId: number): number => {
    if (!dispatchData) return 0;
    const row = (dispatchData.per_pacs || []).find((p: any) => p.po_item_id === poItemId);
    if (!row) return 0;
    const stagedSum = staged.filter((s) => s.po_item_id === poItemId).reduce((a, b) => a + b.bags, 0);
    return Math.max(Number(row.remaining || 0) - stagedSum, 0);
  };

  // Inline add: each PO line item in the per-PACS table has its own bags/invoice
  // inputs in `rowInputs` keyed by po_item_id (same PACS may appear twice with
  // different bag types — they're tracked independently). Pulls trip-level
  // fields (vehicle, e-way, date) from dispatchForm.
  const addStagedRow = (row: any) => {
    if (!dispatchPo) return;
    const input = rowInputs[row.po_item_id] || { bags: 0, invoice_files: [] };
    if (!input.bags || input.bags <= 0) { setPosErr('Enter bags > 0'); return; }
    const cap = remainingFor(row.po_item_id);
    if (input.bags > cap) {
      setPosErr(`Only ${cap.toLocaleString()} bags remaining for this line item.`);
      return;
    }
    // Backend mandates these for any PACS-direct dispatch — fail fast in the UI
    // instead of letting the user stage rows that will all 400 on submit.
    const tripMissing: string[] = [];
    if (!dispatchForm.vehicle_number.trim()) tripMissing.push('Vehicle Number');
    if (!dispatchForm.dispatch_date) tripMissing.push('Dispatch Date');
    if (!dispatchForm.supplier_invoice_no.trim()) tripMissing.push('Supplier Invoice No.');
    if (!dispatchForm.eway_bill_no.trim()) tripMissing.push('E-Way Bill No.');
    if (tripMissing.length) {
      setPosErr(`Fill trip details first: ${tripMissing.join(', ')}`);
      return;
    }
    setStaged((cur) => [...cur, {
      po_item_id: row.po_item_id,
      pacs_id: row.pacs_id,
      pacs_name: row.pacs_name || `pacs#${row.pacs_id}`,
      bag_type: row.bag_type,
      specification: row.specification,
      bags: input.bags,
      vehicle_number: dispatchForm.vehicle_number,
      dispatch_date: dispatchForm.dispatch_date,
      supplier_invoice_no: dispatchForm.supplier_invoice_no,
      supplier_invoice_date: dispatchForm.supplier_invoice_date,
      eway_bill_no: dispatchForm.eway_bill_no,
      condition_at_dispatch: dispatchForm.condition_at_dispatch,
      remarks: dispatchForm.remarks,
      invoice_files: input.invoice_files,
    }]);
    setRowInputs((cur) => ({ ...cur, [row.po_item_id]: { bags: 0, invoice_files: [] } }));
    setPosErr(null);
  };

  const setRowBags = (poItemId: number, bags: number) => {
    setRowInputs((cur) => ({ ...cur, [poItemId]: { bags, invoice_files: cur[poItemId]?.invoice_files || [] } }));
  };
  const addRowFiles = (poItemId: number, files: File[]) => {
    const oversized = files.find((f) => f.size > INVOICE_MAX_BYTES);
    if (oversized) { setPosErr(`"${oversized.name}" exceeds 10 MB`); return; }
    setRowInputs((cur) => ({
      ...cur,
      [poItemId]: { bags: cur[poItemId]?.bags || 0, invoice_files: [...(cur[poItemId]?.invoice_files || []), ...files] },
    }));
    setPosErr(null);
  };
  const removeRowFile = (poItemId: number, idx: number) => {
    setRowInputs((cur) => ({
      ...cur,
      [poItemId]: { bags: cur[poItemId]?.bags || 0, invoice_files: (cur[poItemId]?.invoice_files || []).filter((_, j) => j !== idx) },
    }));
  };

  const removeStaged = (idx: number) => {
    setStaged((cur) => cur.filter((_, i) => i !== idx));
  };

  const stagedFileInputs = useRef<Record<number, HTMLInputElement | null>>({});
  const attachStagedFile = (idx: number, file: File | null) => {
    if (!file) return;
    if (file.size > INVOICE_MAX_BYTES) { setPosErr('Invoice file must be 10 MB or smaller'); return; }
    setPosErr(null);
    setStaged((cur) => cur.map((s, i) => (i === idx ? { ...s, invoice_file: file } : s)));
    if (stagedFileInputs.current[idx]) stagedFileInputs.current[idx]!.value = '';
  };
  const clearStagedFile = (idx: number) => {
    setStaged((cur) => cur.map((s, i) => (i === idx ? { ...s, invoice_file: null } : s)));
  };

  const submitAllStaged = async () => {
    if (!seasonId || !dispatchPo || staged.length === 0) return;
    setSavingDispatch(true);
    setPosErr(null);
    const succeeded: number[] = [];
    const failed: { idx: number; msg: string }[] = [];
    for (let i = 0; i < staged.length; i++) {
      const s = staged[i];
      // Files: per-row when single-invoice mode is OFF, shared when ON.
      const filesForThisRow = singleInvoice.active ? singleInvoice.invoice_files : s.invoice_files;
      try {
        const { data: created } = await gunnyPosAPI.recordDispatch(seasonId, dispatchPo.id, {
          pacs_id: s.pacs_id,
          po_item_id: s.po_item_id,
          bags: s.bags,
          delivered_on: s.dispatch_date,
          vehicle_number: s.vehicle_number || undefined,
          dispatch_date: s.dispatch_date || undefined,
          supplier_invoice_no: s.supplier_invoice_no || undefined,
          supplier_invoice_date: s.supplier_invoice_date || undefined,
          eway_bill_no: s.eway_bill_no || undefined,
          condition_at_dispatch: s.condition_at_dispatch || undefined,
          remarks: s.remarks || undefined,
        });
        const deliveryId = created?.delivery?.id;
        if (deliveryId && filesForThisRow.length > 0) {
          for (const file of filesForThisRow) {
            try {
              await gunnyPosAPI.uploadDeliveryInvoice(seasonId, dispatchPo.id, deliveryId, file);
            } catch (e: any) {
              failed.push({ idx: i, msg: `Row ${i + 1} dispatched but file "${file.name}" upload failed: ${e?.response?.data?.message || 'unknown'}` });
              break;
            }
          }
          if (failed.find((f) => f.idx === i)) continue;
        }
        succeeded.push(i);
      } catch (e: any) {
        failed.push({ idx: i, msg: e?.response?.data?.message || 'Dispatch failed' });
      }
    }
    setSavingDispatch(false);
    if (failed.length === 0) {
      setFlash(`All ${succeeded.length} dispatch row(s) submitted for PO ${dispatchPo.po_number}.`);
      setDispatchPo(null); setDispatchData(null); setStaged([]);
      await loadPos(seasonId);
      setTimeout(() => setFlash(null), 3500);
    } else {
      // Drop the succeeded rows; keep failed staged so user can fix them
      setStaged((cur) => cur.filter((_, i) => !succeeded.includes(i)));
      const { data } = await gunnyPosAPI.fulfillment(seasonId, dispatchPo.id);
      setDispatchData(data);
      setPosErr(`${succeeded.length} submitted, ${failed.length} failed: ${failed[0].msg}`);
    }
  };

  const downloadDeliveryInvoice = async (poId: number, deliveryId: number, invoiceId: number, filename: string) => {
    if (!seasonId) return;
    try {
      const res = await apiClient.get(`/gunny-pos/${seasonId}/${poId}/deliveries/${deliveryId}/invoices/${invoiceId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (e: any) {
      setPosErr(e?.response?.data?.message || 'Could not download invoice');
    }
  };

  const downloadPoPdf = async (po: SupplierPo) => {
    if (!seasonId) return;
    try {
      const res = await gunnyPosAPI.downloadPoPdf(seasonId, po.id);
      const url = window.URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PO-${po.po_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (e: any) {
      setPosErr(e?.response?.data?.message || 'Could not download PO');
    }
  };

  const openEditor = (r: GunnyProformaRow) => {
    if (!seasonId) return;
    setEdit({
      id: r.id,
      seasonId,
      bags_supplied: r.bags_supplied,
      supplier_invoice_no: r.supplier_invoice_no || '',
      supplier_invoice_date: r.supplier_invoice_date || '',
      eway_bill_no: r.eway_bill_no || '',
      bill_submission_date: r.bill_submission_date || new Date().toISOString().slice(0, 10),
      bill_raised_bags: r.bill_raised_bags || r.bags_supplied || 0,
      claimed_rate_per_bag_incl_gst: r.claimed_rate_per_bag_incl_gst || 0,
    });
  };

  const save = async () => {
    if (!edit) return;
    setSaving(true);
    setErr(null);
    try {
      await gunnyUtilizationAPI.supplierClaim(edit.seasonId, edit.id, {
        supplier_invoice_no: edit.supplier_invoice_no || undefined,
        supplier_invoice_date: edit.supplier_invoice_date || undefined,
        eway_bill_no: edit.eway_bill_no || undefined,
        bill_submission_date: edit.bill_submission_date || undefined,
        bill_raised_bags: edit.bill_raised_bags || undefined,
        claimed_rate_per_bag_incl_gst: edit.claimed_rate_per_bag_incl_gst || undefined,
      });
      setFlash('Claim submitted. The District Manager will certify it next.');
      setEdit(null);
      if (seasonId) await load(seasonId);
      setTimeout(() => setFlash(null), 2500);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const fmtMoney = (v: number | null) =>
    v == null ? '—' : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-xl font-bold text-gray-900">Gunny bag claims</h1>
        <p className="text-sm text-gray-600">
          File your invoice and bill-claim details for each batch of gunny bags supplied. Values marked{' '}
          <span className="italic text-gray-500">(auto)</span> are computed on submission.
        </p>
      </div>

      {err && (
        <div className="mb-3 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          <AlertCircle className="w-4 h-4" /> {err}
        </div>
      )}
      {flash && (
        <div className="mb-3 flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
          <CheckCircle className="w-4 h-4" /> {flash}
        </div>
      )}

      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-base font-semibold text-gray-900">My Purchase Orders</h2>
          <span className="text-xs text-gray-500">
            Upload one tax-invoice file (PDF / PNG / JPG, ≤ 10 MB) per PO.
          </span>
        </div>

        {posErr && (
          <div className="mb-2 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            <AlertCircle className="w-4 h-4" /> {posErr}
          </div>
        )}

        <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
          {posLoading ? (
            <div className="p-8 text-center text-gray-500">Loading POs…</div>
          ) : pos.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No purchase orders raised against your account yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr className="text-left text-xs uppercase text-gray-600">
                    <th className="px-3 py-2">PO #</th>
                    <th className="px-3 py-2">PO Date</th>
                    <th className="px-3 py-2">PACS (Delivery Location)</th>
                    <th className="px-3 py-2 text-right">Allotted</th>
                    <th className="px-3 py-2 text-right">Dispatched</th>
                    <th className="px-3 py-2 text-right">Remaining</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Invoices</th>
                    <th className="px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pos.map((p) => {
                    const canDownloadPo = p.status === 'manager_approved' || p.status === 'fulfilled' || p.status === 'closed';
                    const ful = fulfillmentMap.get(p.id);
                    const perPacs: any[] = ful?.per_pacs?.length
                      ? ful.per_pacs
                      : [{ pacs_id: null, pacs_name: p.delivery_location || '—', ordered: p.total_bags, delivered: 0, remaining: p.total_bags, bag_type: '' }];
                    return perPacs.map((row, idx) => (
                      <tr key={`${p.id}-${row.pacs_id || idx}`} className={`border-b ${idx === 0 ? 'border-t-2 border-t-gray-200' : 'border-gray-100'} hover:bg-gray-50`}>
                        {idx === 0 && (
                          <>
                            <td className="px-3 py-2 font-mono text-xs align-top" rowSpan={perPacs.length}>{p.po_number}</td>
                            <td className="px-3 py-2 align-top text-xs" rowSpan={perPacs.length}>{p.po_date || '—'}<div className="text-[10px] text-gray-500">by {p.delivery_date || '—'}</div></td>
                          </>
                        )}
                        <td className="px-3 py-2">{row.pacs_name || `pacs#${row.pacs_id}`}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{Number(row.ordered || 0).toLocaleString()}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{Number(row.delivered || 0).toLocaleString()}</td>
                        <td className={`px-3 py-2 text-right tabular-nums font-medium ${row.remaining === 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                          {Number(row.remaining || 0).toLocaleString()}
                        </td>
                        {idx === 0 && (
                          <>
                            <td className="px-3 py-2 align-top" rowSpan={perPacs.length}>
                              <span className="inline-block px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700 capitalize">
                                {p.status.replace(/_/g, ' ')}
                              </span>
                            </td>
                            <td className="px-3 py-2 align-top" rowSpan={perPacs.length}>
                              {(() => {
                                const dels = poDeliveriesMap.get(p.id) || [];
                                const items: { kind: 'file' | 'no-file'; dispatchId: number; pacsName: string; invId?: number; label: string }[] = [];
                                for (const d of dels) {
                                  const pacsName = d.pacs?.name || (d.godown?.godown_name) || '—';
                                  const invs = d.invoices || [];
                                  if (invs.length > 0) {
                                    for (const inv of invs) items.push({ kind: 'file', dispatchId: d.id, pacsName, invId: inv.id, label: inv.file_name });
                                  } else if (d.invoice_file_name) {
                                    // Legacy single-file column on the delivery row (pre-multi-file era)
                                    items.push({ kind: 'no-file', dispatchId: d.id, pacsName, label: d.invoice_file_name });
                                  } else if (d.supplier_invoice_no) {
                                    // Typed invoice number from older flow, no file uploaded yet
                                    items.push({ kind: 'no-file', dispatchId: d.id, pacsName, label: `# ${d.supplier_invoice_no}` });
                                  }
                                }
                                if (!canDownloadPo) return <span className="text-xs text-gray-400">—</span>;
                                if (items.length === 0) return <span className="text-xs text-gray-400">none</span>;
                                return (
                                  <div className="flex flex-col gap-1 max-w-[280px]">
                                    {items.map((it, i) => {
                                      const shortPacs = it.pacsName.replace(/^PACS |^DCMS /, '').slice(0, 10);
                                      const isClickable = it.kind === 'file';
                                      return (
                                        <button key={i}
                                          onClick={() => isClickable && it.invId && downloadDeliveryInvoice(p.id, it.dispatchId, it.invId, it.label)}
                                          disabled={!isClickable}
                                          title={`${it.pacsName}: ${it.label}${isClickable ? ' (click to download)' : ' (no file uploaded)'}`}
                                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] rounded text-left ${
                                            isClickable
                                              ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-800 cursor-pointer'
                                              : 'bg-gray-100 text-gray-600 cursor-default'
                                          }`}>
                                          {isClickable ? '📎' : '📝'}
                                          <span className="truncate">{it.label.length > 22 ? it.label.slice(0, 19) + '…' : it.label}</span>
                                          <span className={`ml-0.5 ${isClickable ? 'text-emerald-600' : 'text-gray-500'}`}>({shortPacs})</span>
                                        </button>
                                      );
                                    })}
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="px-3 py-2 align-top" rowSpan={perPacs.length}>
                              <div className="flex items-center gap-1">
                                <button onClick={() => downloadPoPdf(p)} disabled={!canDownloadPo}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed rounded text-gray-700"
                                  title={canDownloadPo ? 'Download PO as PDF' : 'Available once the PO is manager-approved'}>
                                  <Download className="w-3 h-3" /> PDF
                                </button>
                                <button onClick={() => openDispatch(p)} disabled={!canDownloadPo}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded"
                                  title={canDownloadPo ? 'Record a dispatch against a PACS' : 'Available once the PO is manager-approved'}>
                                  <Truck className="w-3 h-3" /> Dispatch
                                </button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    ));
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Bill Claims is now a separate page at /supplier/claims */}
      {/* eslint-disable-next-line @typescript-eslint/no-unused-vars */}
      {false && false && (
      <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            No supply records found against your account for the active season.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr className="text-left text-xs uppercase text-gray-600">
                  <th className="px-3 py-2">#</th>
                  <th className="px-3 py-2">District</th>
                  <th className="px-3 py-2">Society</th>
                  <th className="px-3 py-2 text-right">Supplied</th>
                  <th className="px-3 py-2">Invoice No.</th>
                  <th className="px-3 py-2 text-right">Bill Bags</th>
                  <th className="px-3 py-2 text-right">Rate</th>
                  <th className="px-3 py-2 text-right">Claimed Gross</th>
                  <th className="px-3 py-2 text-right">DM Gross</th>
                  <th className="px-3 py-2 text-right">Paid</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-500">{r.sl_no}</td>
                    <td className="px-3 py-2">{r.district}</td>
                    <td className="px-3 py-2">{r.society_name || '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.bags_supplied.toLocaleString()}</td>
                    <td className="px-3 py-2">{r.supplier_invoice_no || <span className="text-gray-400">—</span>}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.bill_raised_bags.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.claimed_rate_per_bag_incl_gst)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.claimed_gross_amount)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.gross_amount)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.payment_released)}</td>
                    <td className="px-3 py-2">
                      <GunnyStepTracker row={r} />
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        onClick={() => setDetail(r)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded mr-1"
                      >
                        <Eye className="w-3 h-3" /> View
                      </button>
                      {r.status === 'draft' ? (
                        <button
                          onClick={() => openEditor(r)}
                          className="inline-flex items-center gap-1 px-3 py-1 text-xs bg-amber-600 hover:bg-amber-700 text-white rounded"
                        >
                          <Pencil className="w-3 h-3" /> Claim
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">locked</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {dispatchPo && dispatchData && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b sticky top-0 bg-white">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-gray-900">Record Dispatch — PO {dispatchPo.po_number}</h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Total ordered: {dispatchData.ordered_total?.toLocaleString()} ·
                    Delivered: {dispatchData.delivered_total?.toLocaleString()} ·
                    Remaining: <span className="font-medium text-amber-700">{dispatchData.remaining_total?.toLocaleString()}</span>
                  </p>
                </div>
                <button onClick={() => { setDispatchPo(null); setDispatchData(null); }} className="text-gray-400 hover:text-gray-600">✕</button>
              </div>
            </div>

            <div className="px-6 py-4">
              {/* Trip-level fields first — they're shared by every row added below. */}
              <div className="mb-5 p-4 border border-blue-200 bg-blue-50/50 rounded-lg">
                <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Truck className="w-4 h-4 text-blue-700" /> Trip details
                  <span className="text-[11px] font-normal text-gray-500">— one vehicle, one e-way bill, one dispatch date for this submission</span>
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Vehicle Number</label>
                    <input type="text" placeholder="TS09 AB 1234" value={dispatchForm.vehicle_number}
                      onChange={(e) => setDispatchForm({ ...dispatchForm, vehicle_number: e.target.value })}
                      className="w-full px-3 py-2 border rounded text-sm bg-white" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">E-Way Bill No.</label>
                    <input type="text" value={dispatchForm.eway_bill_no}
                      onChange={(e) => setDispatchForm({ ...dispatchForm, eway_bill_no: e.target.value })}
                      className="w-full px-3 py-2 border rounded text-sm bg-white" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Dispatch Date</label>
                    <input type="date" value={dispatchForm.dispatch_date}
                      onChange={(e) => setDispatchForm({ ...dispatchForm, dispatch_date: e.target.value })}
                      className="w-full px-3 py-2 border rounded text-sm bg-white" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Supplier Invoice No.</label>
                    <input type="text" value={dispatchForm.supplier_invoice_no}
                      onChange={(e) => setDispatchForm({ ...dispatchForm, supplier_invoice_no: e.target.value })}
                      className="w-full px-3 py-2 border rounded text-sm bg-white" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Supplier Invoice Date</label>
                    <input type="date" value={dispatchForm.supplier_invoice_date}
                      onChange={(e) => setDispatchForm({ ...dispatchForm, supplier_invoice_date: e.target.value })}
                      className="w-full px-3 py-2 border rounded text-sm bg-white" />
                  </div>
                </div>

                <label className="mt-3 flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" checked={singleInvoice.active}
                    onChange={(e) => setSingleInvoice({ ...singleInvoice, active: e.target.checked })} />
                  <span className="font-medium">Use one consolidated invoice for all PACS</span>
                  <span className="text-[11px] text-gray-500">(when the supplier billed the whole truck on a single invoice)</span>
                </label>

                {singleInvoice.active && (
                  <div className="mt-3 border-t border-blue-200 pt-3">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Consolidated invoice file(s) — PDF / PNG / JPG, ≤ 10 MB each
                    </label>
                    <input ref={sharedFileInputRef} type="file" accept={ACCEPTED_INVOICE_TYPES} multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        const oversized = files.find((f) => f.size > INVOICE_MAX_BYTES);
                        if (oversized) { setPosErr(`"${oversized.name}" exceeds 10 MB`); return; }
                        setSingleInvoice({ ...singleInvoice, invoice_files: [...singleInvoice.invoice_files, ...files] });
                        setPosErr(null);
                        e.target.value = '';
                      }}
                      className="block w-full text-xs text-gray-700 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                    {singleInvoice.invoice_files.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {singleInvoice.invoice_files.map((f, i) => (
                          <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] bg-emerald-50 text-emerald-800 rounded">
                            📎 {f.name.length > 22 ? f.name.slice(0, 19) + '…' : f.name}
                            <button onClick={() => setSingleInvoice({ ...singleInvoice, invoice_files: singleInvoice.invoice_files.filter((_, j) => j !== i) })}
                              className="text-rose-500 hover:text-rose-700">×</button>
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="text-[11px] text-gray-500 mt-1">Each file is uploaded to every PACS delivery row in this trip.</p>
                  </div>
                )}
              </div>

              {/* One row per (PACS, bag-type) line item — different bag types
                  have different rates and physical SKUs, so they're tracked
                  independently. Backend keys deliveries by po_item_id. */}
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Per-line-item dispatch</h3>
              <div className="overflow-x-auto mb-4 border border-gray-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr className="text-left text-xs uppercase text-gray-600">
                      <th className="px-3 py-2">PACS</th>
                      <th className="px-3 py-2">Bag type · rate</th>
                      <th className="px-3 py-2 text-right">Ordered</th>
                      <th className="px-3 py-2 text-right">Delivered</th>
                      <th className="px-3 py-2 text-right">Remaining</th>
                      <th className="px-3 py-2 text-right w-28">Bags now</th>
                      {!singleInvoice.active && <th className="px-3 py-2 w-52">Invoice file(s)</th>}
                      <th className="px-3 py-2 w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(dispatchData.per_pacs || []).length === 0 && (
                      <tr><td colSpan={singleInvoice.active ? 6 : 7} className="px-3 py-4 text-center text-gray-500 text-xs">This PO has no per-PACS allocations.</td></tr>
                    )}
                    {(dispatchData.per_pacs || []).map((row: any) => {
                      const cap = remainingFor(row.po_item_id);
                      const input = rowInputs[row.po_item_id] || { bags: 0, invoice_files: [] };
                      const delivered = Number(row.received || 0) + Number(row.pending_ack || 0);
                      const exceeds = input.bags > cap;
                      const rate = Number(row.rate || 0);
                      return (
                        <tr key={row.po_item_id} className={`border-b last:border-0 align-middle ${input.bags > 0 ? 'bg-blue-50/40' : ''}`}>
                          <td className="px-3 py-2">
                            <div className="font-medium text-gray-800">{row.pacs_name || `pacs#${row.pacs_id}`}</div>
                            {row.commodity_name && (
                              <div className="text-[10px] text-emerald-700 bg-emerald-50 inline-block px-1.5 py-0.5 rounded mt-0.5">
                                {row.commodity_name}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <div className="text-gray-800">{bagTypeLabel(row.bag_type)}{row.specification ? ` · ${row.specification}` : ''}</div>
                            <div className="text-[11px] text-gray-500">₹ {rate.toFixed(2)} / bag</div>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">{Number(row.ordered || 0).toLocaleString()}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{delivered.toLocaleString()}</td>
                          <td className={`px-3 py-2 text-right tabular-nums font-medium ${cap === 0 ? 'text-emerald-700' : 'text-amber-700'}`}>{cap.toLocaleString()}</td>
                          <td className="px-3 py-2">
                            <input type="number" min={0} max={cap} disabled={cap === 0}
                              value={input.bags || ''}
                              onChange={(e) => setRowBags(row.po_item_id, Number(e.target.value) || 0)}
                              placeholder="0"
                              className={`w-full px-2 py-1.5 border-2 rounded text-sm tabular-nums text-right focus:outline-none ${
                                exceeds ? 'border-rose-400 bg-rose-50' :
                                input.bags > 0 ? 'border-blue-400' : 'border-gray-200'
                              } disabled:bg-gray-50`} />
                          </td>
                          {!singleInvoice.active && (
                            <td className="px-3 py-2">
                              <input type="file" accept={ACCEPTED_INVOICE_TYPES} multiple
                                onChange={(e) => {
                                  addRowFiles(row.po_item_id, Array.from(e.target.files || []));
                                  e.target.value = '';
                                }}
                                className="block w-full text-[11px] text-gray-700 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                              {input.invoice_files.length > 0 && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {input.invoice_files.map((f, i) => (
                                    <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] bg-emerald-50 text-emerald-800 rounded">
                                      📎 {f.name.length > 14 ? f.name.slice(0, 11) + '…' : f.name}
                                      <button onClick={() => removeRowFile(row.po_item_id, i)} className="text-rose-500 hover:text-rose-700">×</button>
                                    </span>
                                  ))}
                                </div>
                              )}
                            </td>
                          )}
                          <td className="px-3 py-2 text-center">
                            <button onClick={() => addStagedRow(row)}
                              disabled={!input.bags || input.bags <= 0 || exceeds}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white rounded">
                              <Plus className="w-3 h-3" /> Add
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {staged.length > 0 && (
                <div className="mt-5 border-t pt-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                    Staged rows ({staged.length}) — review before final submit
                  </h3>
                  <p className="text-xs text-gray-500 mb-2">
                    Trip: <span className="font-mono">{dispatchForm.vehicle_number || '—'}</span>
                    {' · '}E-Way: <span className="font-mono">{dispatchForm.eway_bill_no || '—'}</span>
                    {' · '}Date: {dispatchForm.dispatch_date || '—'}
                    {singleInvoice.active && singleInvoice.invoice_files.length > 0 && (
                      <span className="text-emerald-700">
                        {' · '}Consolidated: {singleInvoice.invoice_files.map((f) => `📎 ${f.name}`).join(' · ')}
                      </span>
                    )}
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border">
                      <thead className="bg-gray-50 border-b">
                        <tr className="text-left text-xs uppercase text-gray-600">
                          <th className="px-2 py-1.5">#</th>
                          <th className="px-2 py-1.5">PACS</th>
                          <th className="px-2 py-1.5 text-right">Bags</th>
                          {!singleInvoice.active && <th className="px-2 py-1.5">Invoices</th>}
                          <th className="px-2 py-1.5"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {staged.map((s, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="px-2 py-1.5 text-gray-500">{i + 1}</td>
                            <td className="px-2 py-1.5">
                              <div>{s.pacs_name}</div>
                              {(s as any).bag_type && (
                                <div className="text-[10px] text-gray-500">
                                  {bagTypeLabel((s as any).bag_type)}{(s as any).specification ? ` · ${(s as any).specification}` : ''}
                                </div>
                              )}
                            </td>
                            <td className="px-2 py-1.5 text-right tabular-nums font-medium">{s.bags.toLocaleString()}</td>
                            {!singleInvoice.active && (
                              <td className="px-2 py-1.5 text-xs">
                                {s.invoice_files.length === 0 ? (
                                  <span className="text-gray-400">none</span>
                                ) : (
                                  <div className="flex flex-wrap gap-1">
                                    {s.invoice_files.map((f, j) => (
                                      <span key={j} className="inline-block px-1.5 py-0.5 bg-emerald-50 text-emerald-800 rounded text-[11px]">
                                        📎 {f.name.length > 18 ? f.name.slice(0, 15) + '…' : f.name}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </td>
                            )}
                            <td className="px-2 py-1.5 text-right">
                              <button onClick={() => removeStaged(i)} className="text-rose-500 hover:text-rose-700" title="Remove row">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                        <tr className="bg-gray-50 font-semibold">
                          <td className="px-2 py-1.5" colSpan={2}>Total</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{staged.reduce((a, b) => a + b.bags, 0).toLocaleString()}</td>
                          <td colSpan={singleInvoice.active ? 1 : 2}></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t flex justify-between items-center gap-2 bg-gray-50 sticky bottom-0">
              <span className="text-xs text-gray-500">
                {staged.length === 0 ? 'No rows added yet — fill the form and click "Add to dispatch".' : `${staged.length} row(s) staged · ${staged.reduce((a, b) => a + b.bags, 0).toLocaleString()} bags total`}
              </span>
              <div className="flex gap-2">
                <button onClick={() => { setDispatchPo(null); setDispatchData(null); setStaged([]); }} className="px-4 py-2 text-sm hover:bg-gray-100 rounded">Close</button>
                <button
                  onClick={submitAllStaged}
                  disabled={savingDispatch || staged.length === 0}
                  className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded"
                >
                  {savingDispatch ? 'Submitting…' : `Submit ${staged.length || ''} dispatch${staged.length === 1 ? '' : 'es'}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ClaimModal: React.FC<{
  edit: EditState;
  setEdit: (e: EditState) => void;
  save: () => void;
  saving: boolean;
  onClose: () => void;
}> = ({ edit, setEdit, save, saving, onClose }) => {
  const gross = +(edit.bill_raised_bags * edit.claimed_rate_per_bag_incl_gst || 0).toFixed(2);
  const cgst = +(gross / 21 / 2).toFixed(2);
  const net = +(gross - cgst * 2).toFixed(2);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
        <div className="px-6 py-4 border-b">
          <h2 className="font-semibold text-gray-900">File bill claim</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Against {edit.bags_supplied.toLocaleString()} bags supplied.
          </p>
        </div>
        <div className="px-6 py-5 grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Invoice No.</label>
            <input
              type="text"
              value={edit.supplier_invoice_no}
              onChange={(e) => setEdit({ ...edit, supplier_invoice_no: e.target.value })}
              className="w-full px-3 py-2 border rounded text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date</label>
            <input
              type="date"
              value={edit.supplier_invoice_date}
              onChange={(e) => setEdit({ ...edit, supplier_invoice_date: e.target.value })}
              className="w-full px-3 py-2 border rounded text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">E-way Bill No.</label>
            <input
              type="text"
              value={edit.eway_bill_no}
              onChange={(e) => setEdit({ ...edit, eway_bill_no: e.target.value })}
              className="w-full px-3 py-2 border rounded text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bill Submission Date</label>
            <input
              type="date"
              value={edit.bill_submission_date}
              onChange={(e) => setEdit({ ...edit, bill_submission_date: e.target.value })}
              className="w-full px-3 py-2 border rounded text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bags on Bill</label>
            <input
              type="number"
              min={0}
              value={edit.bill_raised_bags}
              onChange={(e) => setEdit({ ...edit, bill_raised_bags: Number(e.target.value) || 0 })}
              className="w-full px-3 py-2 border rounded text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rate per bag (incl. GST)</label>
            <input
              type="number"
              step="0.01"
              min={0}
              value={edit.claimed_rate_per_bag_incl_gst}
              onChange={(e) => setEdit({ ...edit, claimed_rate_per_bag_incl_gst: Number(e.target.value) || 0 })}
              className="w-full px-3 py-2 border rounded text-sm"
            />
          </div>
        </div>

        <div className="px-6 pb-4 border-t pt-3 text-sm bg-gray-50">
          <div className="flex justify-between">
            <span className="text-gray-600">Gross <span className="text-gray-400 italic">(auto)</span></span>
            <span className="tabular-nums font-semibold">₹ {gross.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between text-xs text-gray-600 mt-0.5">
            <span>CGST ≈ SGST ≈ Gross / 42</span>
            <span className="tabular-nums">₹ {cgst.toLocaleString('en-IN', { minimumFractionDigits: 2 })} each</span>
          </div>
          <div className="flex justify-between text-xs text-gray-600 mt-0.5">
            <span>Net</span>
            <span className="tabular-nums">₹ {net.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
        </div>

        <div className="px-6 py-4 border-t flex justify-end gap-2 bg-gray-50">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving || edit.bill_raised_bags <= 0 || edit.claimed_rate_per_bag_incl_gst <= 0}
            className="px-4 py-2 text-sm bg-amber-600 hover:bg-amber-700 text-white rounded disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Submit claim'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupplierDashboard;
